import React, { useRef } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

// Import motion preset videos for local thumbnail previews
import style1Video from "@/assets/motion-presets/style-1.mp4";
import robotVideo from "@/assets/motion-presets/style-robot.mp4";
import balletVideo from "@/assets/motion-presets/style-ballet.mp4";
import style4Video from "@/assets/motion-presets/style-4.mp4";
import style5Video from "@/assets/motion-presets/style-5.mp4";

// Public Supabase Storage URLs for API calls (Replicate needs public URIs)
const SUPABASE_STORAGE_BASE = "https://hqyjiuoavgrfghwqhkih.supabase.co/storage/v1/object/public/videos/motion-presets";

interface MotionPreset {
  id: string;
  name: string;
  previewUrl: string;  // Local asset for fast thumbnail loading
  publicUrl: string;   // Supabase URL for API calls
  duration: number;
}

const MOTION_PRESETS: MotionPreset[] = [
  { id: 'style-1', name: 'Style 1', previewUrl: style1Video, publicUrl: `${SUPABASE_STORAGE_BASE}/style-1.mp4`, duration: 5 },
  { id: 'robot', name: 'Robot', previewUrl: robotVideo, publicUrl: `${SUPABASE_STORAGE_BASE}/style-robot.mp4`, duration: 5 },
  { id: 'ballet', name: 'Ballet', previewUrl: balletVideo, publicUrl: `${SUPABASE_STORAGE_BASE}/style-ballet.mp4`, duration: 5 },
  { id: 'style-4', name: 'Style 4', previewUrl: style4Video, publicUrl: `${SUPABASE_STORAGE_BASE}/style-4.mp4`, duration: 5 },
  { id: 'style-5', name: 'Style 5', previewUrl: style5Video, publicUrl: `${SUPABASE_STORAGE_BASE}/style-5.mp4`, duration: 5 },
];

interface MotionPresetGalleryProps {
  selectedPreset: string | null;
  onSelect: (videoUrl: string, duration: number) => void;
  onClear: () => void;
}

export function MotionPresetGallery({ selectedPreset, onSelect, onClear }: MotionPresetGalleryProps) {
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);

  const handleMouseEnter = (index: number) => {
    const video = videoRefs.current[index];
    if (video) {
      video.play().catch(() => {});
    }
  };

  const handleMouseLeave = (index: number) => {
    const video = videoRefs.current[index];
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
  };

  const isPresetUrl = (url: string) => {
    return MOTION_PRESETS.some(p => p.publicUrl === url);
  };

  return (
    <div className="flex gap-1.5">
      {MOTION_PRESETS.map((preset, index) => {
        // Check if selected by comparing against the public URL (what gets passed to API)
        const isSelected = selectedPreset === preset.publicUrl;
        
        return (
          <div
            key={preset.id}
            className={cn(
              "relative cursor-pointer rounded-lg overflow-hidden transition-all",
              isSelected 
                ? "ring-2 ring-green-500 ring-offset-1 ring-offset-background" 
                : "ring-1 ring-border hover:ring-primary/50"
            )}
            onClick={() => !isSelected && onSelect(preset.publicUrl, preset.duration)}
            onMouseEnter={() => handleMouseEnter(index)}
            onMouseLeave={() => handleMouseLeave(index)}
          >
            <video
              ref={el => videoRefs.current[index] = el}
              className="w-10 h-14 object-cover"
              src={preset.previewUrl}
              muted
              loop
              playsInline
              preload="metadata"
            />
            {isSelected && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onClear();
                }}
                className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground rounded-full p-0.5 shadow-sm"
              >
                <X className="h-3 w-3" />
              </button>
            )}
            <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-[8px] text-white text-center py-0.5 truncate px-0.5">
              {preset.name}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Export utility to check if a URL is a preset (checks public URLs)
export function isMotionPreset(url: string): boolean {
  return MOTION_PRESETS.some(p => p.publicUrl === url);
}
